/**
 * Public-page header. Used on Landing, Pricing, Auth, and the public-report
 * surface (/r/:token).
 *
 * Desktop: logo + nav. "Solutions" and "Developers" open hover/focus
 * mega-menus with grouped, described links; the rest are plain links.
 * Mobile: a hamburger opens a full-height Sheet drawer with the same nav
 * (Solutions/Developers expand inline) plus the auth CTAs.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  Menu,
  ChevronDown,
  Activity,
  ShieldCheck,
  Send,
  Building2,
  Boxes,
  Plug,
  Wrench,
  GitBranch,
  Code as CodeIcon,
  Bot,
  ArrowRight,
} from 'lucide-react';
import { IGMark } from '@/components/IGMark';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Sheet, SheetContent, SheetClose } from '@/components/ui/sheet';
import { useAuth } from '@/lib/auth/AuthProvider';
import { analytics } from '@/lib/analytics';
import { cn } from '@/lib/utils';

interface Props {
  /** Hide the right-side CTAs when the surrounding page already has them. */
  minimal?: boolean;
}

type MegaItem = { to: string; label: string; desc: string; icon: typeof Activity };
type MegaMenu = { label: string; items: MegaItem[]; footer?: { to: string; label: string } };

const SOLUTIONS_MENU: MegaMenu = {
  label: 'Solutions',
  items: [
    { to: '/solutions/email-deliverability-monitoring', label: 'Deliverability monitoring', desc: 'Continuous SPF, DKIM, and DMARC scoring', icon: Activity },
    { to: '/solutions/dmarc-monitoring', label: 'DMARC monitoring', desc: 'RUA ingest and the path to p=reject', icon: ShieldCheck },
    { to: '/solutions/transactional-email', label: 'Transactional email', desc: 'Keep receipts and resets in the inbox', icon: Send },
    { to: '/solutions/agencies', label: 'For agencies', desc: 'Every client domain in one view', icon: Building2 },
    { to: '/solutions/saas', label: 'For SaaS', desc: 'Scale sending without scaling risk', icon: Boxes },
  ],
  footer: { to: '/solutions', label: 'All solutions' },
};

const DEVELOPERS_MENU: MegaMenu = {
  label: 'Developers',
  items: [
    { to: '/developers#connect', label: 'MCP server', desc: 'Connect Claude, ChatGPT, or Cursor', icon: Plug },
    { to: '/developers#tools', label: 'The 28 tools', desc: 'Everything an agent can scan and fix', icon: Wrench },
    { to: '/developers#fix-loop', label: 'The fix loop', desc: 'scan → plan → apply, revalidated', icon: GitBranch },
    { to: '/api-docs', label: 'REST API', desc: 'Endpoints, auth, and webhooks', icon: CodeIcon },
    { to: '/developers#install', label: 'SDKs & install', desc: 'npm package and agent skills', icon: Boxes },
    { to: '/developers#discovery', label: 'For AI agents', desc: 'llms.txt, agents.md, and OpenAPI', icon: Bot },
  ],
  footer: { to: '/developers', label: 'Developers hub' },
};

const PLAIN_LINKS = [
  { to: '/pricing', label: 'Pricing' },
  { to: '/guides', label: 'Guides' },
  { to: '/contact', label: 'Contact' },
];

export function PublicHeader({ minimal }: Props) {
  const auth = useAuth();
  const signedIn = !!auth.user;
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  // Close the mega-menu whenever the route changes.
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  const open = (label: string) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  // Small close delay so moving the cursor from the trigger to the panel
  // doesn't flicker the menu shut.
  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 120);
  };

  return (
    <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between gap-2 px-4">
        <Link
          to="/"
          className="flex items-center gap-2 text-xl font-semibold tracking-tight transition-opacity hover:opacity-80"
        >
          <IGMark className="h-7 w-7 text-foreground" />
          InboxGuard
        </Link>

        {/* Desktop nav */}
        {!minimal && (
          <nav className="hidden items-center gap-1 lg:flex" onMouseLeave={scheduleClose}>
            {[SOLUTIONS_MENU, DEVELOPERS_MENU].map((menu) => (
              <div key={menu.label} className="relative" onMouseEnter={() => open(menu.label)}>
                <button
                  type="button"
                  aria-expanded={openMenu === menu.label}
                  onClick={() => setOpenMenu((v) => (v === menu.label ? null : menu.label))}
                  className={cn(
                    'flex items-center gap-1 rounded-md px-3 py-1.5 text-sm transition-colors',
                    openMenu === menu.label
                      ? 'bg-secondary text-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                  )}
                >
                  {menu.label}
                  <ChevronDown
                    className={cn('h-3.5 w-3.5 transition-transform', openMenu === menu.label && 'rotate-180')}
                  />
                </button>
              </div>
            ))}
            {PLAIN_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onMouseEnter={() => setOpenMenu(null)}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}

        {/* Right cluster */}
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {!minimal && (
            <>
              <div className="hidden items-center gap-2 sm:flex">
                {signedIn ? (
                  <Button asChild size="sm">
                    <Link to="/dashboard">Dashboard</Link>
                  </Button>
                ) : (
                  <>
                    <Link
                      to="/auth"
                      className="hidden rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground lg:inline-flex"
                    >
                      Sign in
                    </Link>
                    <Button asChild size="sm">
                      <Link to="/auth?mode=sign-up" onClick={() => analytics.trackCtaClick('signup', 'header')}>
                        Get started
                      </Link>
                    </Button>
                  </>
                )}
              </div>
              {/* Hamburger — below lg */}
              <button
                type="button"
                aria-label="Open menu"
                onClick={() => setMobileOpen(true)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Desktop mega-menu panel */}
      {!minimal && openMenu && (
        <div
          className="absolute inset-x-0 top-full hidden justify-center px-4 lg:flex"
          onMouseEnter={() => open(openMenu)}
          onMouseLeave={scheduleClose}
        >
          <div className="w-full max-w-3xl origin-top animate-fade-in-up rounded-xl border bg-card p-3 shadow-xl ring-1 ring-border/50">
            <MegaGrid menu={openMenu === SOLUTIONS_MENU.label ? SOLUTIONS_MENU : DEVELOPERS_MENU} />
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="right" className="w-[88%] max-w-sm p-0">
          <div className="flex items-center gap-2 border-b px-5 py-4 text-lg font-semibold tracking-tight">
            <IGMark className="h-6 w-6 text-foreground" />
            InboxGuard
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <MobileGroup menu={SOLUTIONS_MENU} />
            <MobileGroup menu={DEVELOPERS_MENU} />
            <div className="my-2 border-t" />
            {PLAIN_LINKS.map((l) => (
              <SheetClose asChild key={l.to}>
                <Link
                  to={l.to}
                  className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-secondary"
                >
                  {l.label}
                </Link>
              </SheetClose>
            ))}
          </div>
          <div className="border-t p-4">
            {signedIn ? (
              <SheetClose asChild>
                <Button asChild className="w-full">
                  <Link to="/dashboard">Go to dashboard</Link>
                </Button>
              </SheetClose>
            ) : (
              <div className="flex flex-col gap-2">
                <SheetClose asChild>
                  <Button asChild className="w-full">
                    <Link to="/auth?mode=sign-up" onClick={() => analytics.trackCtaClick('signup', 'mobile-menu')}>
                      Get started
                    </Link>
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/auth">Sign in</Link>
                  </Button>
                </SheetClose>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}

function MegaGrid({ menu }: { menu: MegaMenu }) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-1">
        {menu.items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="group flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-secondary"
          >
            <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <item.icon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">{item.label}</span>
              <span className="block text-xs leading-snug text-muted-foreground">{item.desc}</span>
            </span>
          </Link>
        ))}
      </div>
      {menu.footer && (
        <Link
          to={menu.footer.to}
          className="mt-1 flex items-center justify-between rounded-lg border-t px-3 pb-1 pt-3 text-sm font-medium text-primary hover:underline"
        >
          {menu.footer.label}
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

function MobileGroup({ menu }: { menu: MegaMenu }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-secondary"
      >
        {menu.label}
        <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded && (
        <div className="mb-1 ml-3 space-y-0.5 border-l pl-3">
          {menu.items.map((item) => (
            <SheetClose asChild key={item.to}>
              <Link to={item.to} className="flex items-center gap-2.5 rounded-md px-2 py-2 hover:bg-secondary">
                <item.icon className="h-4 w-4 flex-shrink-0 text-primary" />
                <span className="text-sm text-foreground">{item.label}</span>
              </Link>
            </SheetClose>
          ))}
          {menu.footer && (
            <SheetClose asChild>
              <Link to={menu.footer.to} className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium text-primary hover:bg-secondary">
                {menu.footer.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </SheetClose>
          )}
        </div>
      )}
    </div>
  );
}
